import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Box, HStack, Text, useMediaQuery, VStack } from '@chakra-ui/react'
import { observer } from 'mobx-react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import routes from 'routes'
import { useStores } from 'hooks/useStores'
import { useActivePlayProfile } from 'hooks/use-active-play-profile'
import GlobalSpinner from 'components/GlobalSpinner'
import { ActiveProfileType } from 'configs/constants/enums/auth'
import PlayerBanner from 'components/DashboardBanners/PlayerBanner'
import OrgTeamBanner from 'components/DashboardBanners/OrgTeamBanner'
import OrgTeamSwitcher from 'components/DashboardBanners/OrgTeamSwitcher'
import { useOrgTeamTree } from 'components/DashboardBanners/OrgTeamSwitcher/use-org-team-tree'
import DashboardHeader from './components/dashboard-header'
import ProfileSwitcher from './components/ProfileSwitcher'
import PlayDashboardBell from './components/play-dashboard-bell'
import StreakBanner from './components/streak-banner'
import QuickStatsGrid from './components/quick-stats-grid'
import NextUpFeatured from './components/next-up-featured'
import CalendarTodoSection from './components/calendar-todo-section'
import DesktopShell from './components/desktop-shell'
import NextUpScrollRow from './components/next-up-scroll-row'
import CalendarPanel from './components/calendar-panel'
import TodoSidebar from './components/todo-sidebar'
import CompleteProfileCard from './components/CompleteProfileCard'
import ScopeErrorNotice from './components/scope-error-notice'
import { readDashboardScope, writeDashboardScope } from './dashboard-scope-storage'
import { athleteProfileToPreview } from 'pages/PlayProfilePage/athlete-profile-to-preview'
import type { ProfilePreview } from 'pages/PlayProfilePage/profile-preview.mock'
import {
  PLAYER_BADGE_BG,
  PLAYER_BADGE_COLOR,
  PLAY_DASHBOARD_BODY_BG,
  YOUTH_BADGE_BG,
  YOUTH_BADGE_COLOR,
  type DashboardData
} from './mock-data'
import { usePlayDashboardData } from './use-play-dashboard-data'
import { visibleRange, type CalendarViewMode } from './calendar-layout'
import WelcomeJourneyModal, { parseWelcomeVariant } from 'components/WelcomeJourneyModal'
import { EMembershipStatus } from 'constants/membership'

interface IBadgeTheme {
  bg: string
  color: string
}

const badgeThemeForRole = (role: string | undefined): IBadgeTheme =>
  role === 'child'
    ? { bg: YOUTH_BADGE_BG, color: YOUTH_BADGE_COLOR }
    : { bg: PLAYER_BADGE_BG, color: PLAYER_BADGE_COLOR }

interface IShellProps {
  data: DashboardData
  greetingName: string
  roleLabel: string
  badgeTheme: IBadgeTheme
  profiles: ReturnType<typeof useActivePlayProfile>['profiles']
  active: ReturnType<typeof useActivePlayProfile>['active']
  setActive: ReturnType<typeof useActivePlayProfile>['setActive']
  activeOrgId: string
  isProfileIncomplete: boolean
  /** Active athlete preview for the dashboard card; null for coach/no athlete. */
  profilePreview: ProfilePreview | null
  /** Organizations the player is rostered with — the banner's PLAYING FOR list. */
  playingFor: string[]
  /** The selected context could not be read; null when it simply holds nothing. */
  scopeError: string | null
  /** The Organization/Team switcher, under the player card. */
  scopeSwitcher: ReactNode
  /**
   * The context banner for the switcher's selection — organization name alone for
   * "All teams", organization label + Team headline once a Team is picked (with
   * Manage team for a coach), coloured by that organization. Null under "All
   * organizations", where there is no single context to name.
   */
  scopeBanner: ReactNode | null
  /** Coach: the context banner leads and the filter sits under it. */
  isCoachView: boolean
}

const MobileShell = ({
  data,
  greetingName,
  roleLabel,
  badgeTheme,
  profiles,
  active,
  setActive,
  activeOrgId,
  isProfileIncomplete,
  profilePreview,
  playingFor,
  scopeError,
  scopeSwitcher,
  scopeBanner,
  isCoachView
}: IShellProps) => {
  return (
    <Box
      minHeight="100vh"
      width="100%"
      bg={PLAY_DASHBOARD_BODY_BG}
      display="flex"
      justifyContent="center"
      pt={{ base: 4, md: 8 }}
      pb="96px"
      sx={{
        '*': { WebkitTapHighlightColor: 'transparent' },
        'button:focus, button:focus-visible, button:active, [role="button"]:focus, [role="button"]:focus-visible, [tabindex]:focus, [tabindex]:focus-visible, .chakra-menu__menu-button:focus':
          {
            outline: 'none !important',
            boxShadow: 'none !important'
          }
      }}
    >
      <Box width="100%" maxWidth="480px" px={4}>
        <VStack spacing="16px" align="stretch">
          {/* Welcome greeting — lives in the body per design (header is icons-only) */}
          <HStack spacing="10px" align="center">
            <Text color="#F2F7FA" fontSize="20px" fontWeight={600} lineHeight="28px" m={0} noOfLines={1}>
              Welcome{greetingName ? `, ${greetingName}` : ''}
            </Text>
            {active && (
              <Box
                px="10px"
                h="22px"
                borderRadius="6px"
                bg={badgeTheme.bg}
                display="inline-flex"
                alignItems="center"
                flexShrink={0}
              >
                <Text fontSize="10px" fontWeight={700} color={badgeTheme.color} m={0} letterSpacing="0.10em">
                  {roleLabel}
                </Text>
              </Box>
            )}
          </HStack>

          {/* Athlete only: incomplete → "Complete your profile" nudge; complete →
              the same PlayerBanner the desktop header carries, so the identity
              block reads the same on both widths rather than being a second card
              that has to be kept in step with it. */}
          {profilePreview &&
            (isProfileIncomplete ? (
              <CompleteProfileCard />
            ) : (
              <PlayerBanner
                playerName={profilePreview.fullName ?? greetingName}
                positionLine={profilePreview.positionLine}
                avatarUrl={profilePreview.avatarUrl}
                playingFor={playingFor}
              />
            ))}

          {/* Both follow the same page-local selection, so they never name
              different contexts. A coach reads the context banner first with the
              filter under it, like every other coach page; a player picks first
              and the banner below follows. */}
          {isCoachView ? (
            <>
              {scopeBanner}
              {scopeSwitcher}
            </>
          ) : (
            <>
              {scopeSwitcher}
              {scopeBanner}
            </>
          )}

          {/* Always shown — renders the zero state (0-day streak) until data loads. */}
          <StreakBanner plan={data.rallyDay} />

          {scopeError && <ScopeErrorNotice message={scopeError} />}

          <Box>
            <Text color="#F4F8FB" fontSize="12px" fontWeight={500} lineHeight="16px" m={0} mb="10px">
              Quick Stats
            </Text>
            <QuickStatsGrid stats={data.quickStats} />
          </Box>

          {data.rallyDay.routineTitle && (
            <Box>
              <HStack justify="space-between" align="baseline" mb="8px">
                <Text color="white" fontSize="16px" fontWeight={600} m={0}>
                  Next Up
                </Text>
                <Text color="rgba(255,255,255,0.55)" fontSize="12px" m={0}>
                  {data.rallyDay.nextUpTime}
                </Text>
              </HStack>
              <NextUpFeatured plan={data.rallyDay} />
            </Box>
          )}

          <CalendarTodoSection
            workouts={data.workouts}
            todos={data.todos}
            teamFilters={data.teamFilters}
            activeOrgId={activeOrgId}
          />
        </VStack>
      </Box>
      {/* BottomNav mounted globally in VerticalLayout — no per-page render. */}
    </Box>
  )
}

const PlayDashboardPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { profiles, active, setActive, raw } = useActivePlayProfile()
  const { authStore, profileStore } = useStores()

  const welcomeVariant = parseWelcomeVariant(searchParams.get('welcome'))
  // Names the Organization the invitation just put the user in. Joined rows come back
  // without a PENDING status; if the list hasn't loaded yet the modal uses generic copy.
  const joinedOrgName = authStore.memberships.find(m => m.status !== EMembershipStatus.PENDING)?.orgName

  const handleCloseWelcome = () => {
    navigate(location.pathname, { replace: true })
  }

  // Paint the body + content-page surfaces dark while this page is mounted so
  // the player widgets sit on a matching backdrop. Cleanup explicitly reverts
  // the inline styles so navigating to /collections etc. doesn't leak the
  // dark backdrop onto other routes (the previous <Global> approach left
  // residue in some unmount orderings).
  useEffect(() => {
    const originalBody = document.body.style.background
    document.body.style.background = PLAY_DASHBOARD_BODY_BG
    const contentPages = document.querySelectorAll<HTMLElement>('.content-page')
    const originalContents: string[] = []
    contentPages.forEach(el => {
      originalContents.push(el.style.background)
      el.style.background = PLAY_DASHBOARD_BODY_BG
    })
    return () => {
      document.body.style.background = originalBody
      contentPages.forEach((el, i) => {
        el.style.background = originalContents[i] ?? ''
      })
    }
  }, [])

  // Scope selection for the banner + the lists below it. `null` means "all".
  // One source of truth for the desktop header switcher and the mobile one, so
  // resizing never reveals a stale choice.
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)
  const { organizations, teamsByOrg, isLoading: isTreeLoading } = useOrgTeamTree()

  const isCoachView = profileStore.activeProfile?.type === ActiveProfileType.COACH

  // Restores the context the coach was last in, then falls back to the first
  // organization in the list — the banner has nothing to show without one, and the
  // dropdown order is the only ordering there is (no ranking by role).
  //
  // Runs once. "All organizations" is a real choice for a coach (the stats
  // aggregate across every organization they are staff in) and it also reads as
  // `null`, so re-running this would snap that selection straight back to the
  // first organization and make the option unusable.
  const hasResolvedScope = useRef(false)
  // Gates the first coach request. Without it the render where the switcher's
  // list arrives — but before this effect resolves the scope — fires an
  // all-organizations query that the very next render throws away, and that query
  // is the most expensive one in the feature.
  const [isScopeSettled, setIsScopeSettled] = useState(false)
  useEffect(() => {
    // Waits on the list rather than on its length, so a coach who belongs to no
    // organization still settles instead of sitting under a spinner.
    if (!isCoachView || hasResolvedScope.current || isTreeLoading) return
    hasResolvedScope.current = true

    // A remembered scope is honoured only if it still exists. Memberships change,
    // Teams get archived, and the other profile on this account has a different
    // tree entirely — restoring an organization the coach is no longer staff in
    // would ask the server for a context it will refuse.
    const stored = readDashboardScope()
    // "All organizations" is always available; a named organization only if the
    // coach is still staff in it.
    const canRestore = stored !== null && (stored.orgId === null || organizations.some(o => o.id === stored.orgId))

    if (canRestore && stored) {
      setSelectedOrgId(stored.orgId)
      const teams = stored.orgId === null ? [] : (teamsByOrg.get(stored.orgId) ?? [])
      setSelectedTeamId(teams.some(t => t.id === stored.teamId) ? stored.teamId : null)
    } else if (organizations.length > 0) {
      setSelectedOrgId(organizations[0].id)
    }
    setIsScopeSettled(true)
  }, [isCoachView, isTreeLoading, organizations, teamsByOrg])

  // The calendar's Day / Week / Month mode and selected date. Held here rather
  // than in the panel, which unmounts under the page spinner whenever the
  // Organization/Team changes — the view the user picked must survive that. The
  // fetch window is derived from them, so the data follows the view.
  const [calendarMode, setCalendarMode] = useState<CalendarViewMode>('day')
  const [calendarDate, setCalendarDate] = useState<Date>(() => new Date())
  const calendarRange = useMemo(() => visibleRange(calendarMode, calendarDate), [calendarMode, calendarDate])

  // Use useMediaQuery (not useBreakpointValue) — the latter returns undefined on first render
  // under { ssr: false } and never resolves to 'desktop' on a freshly mounted SPA route.
  const [isDesktop] = useMediaQuery('(min-width: 767px)', {
    ssr: false,
    fallback: typeof window !== 'undefined' && window.innerWidth >= 767
  })

  // Both widths read the page-local Organization/Team selection, so resizing
  // never reveals a different context than the one just picked.
  const scopeOrgId = selectedOrgId
  const scopeTeamId = selectedTeamId
  const isScopeReady = isScopeSettled

  // Both roles are scoped server-side by this selection, so a switch re-reads
  // the data rather than filtering what is already here.
  const {
    data,
    isLoading,
    error: scopeError
  } = usePlayDashboardData(scopeOrgId, scopeTeamId, isScopeReady, calendarRange)

  const selectedOrg = organizations.find(o => o.id === selectedOrgId) ?? null
  const orgTeams = selectedOrgId === null ? [] : (teamsByOrg.get(selectedOrgId) ?? [])
  const selectedTeam = orgTeams.find(t => t.id === selectedTeamId) ?? null

  // Changing organization drops the team with it — keeping it would leave the
  // banner showing a team that belongs to a different organization.
  const handleOrgChange = (orgId: number | null) => {
    setSelectedOrgId(orgId)
    setSelectedTeamId(null)
    writeDashboardScope({ orgId, teamId: null })
  }

  const handleTeamChange = (teamId: number | null) => {
    setSelectedTeamId(teamId)
    writeDashboardScope({ orgId: selectedOrgId, teamId })
  }

  // The calendar section still speaks the original
  // `'all' | orgId` string contract. Deriving it here keeps them untouched while
  // the selection itself stays a single typed value.
  const activeOrgId = scopeOrgId === null ? 'all' : String(scopeOrgId)

  const roleLabel = (active?.role ?? 'player').toUpperCase()
  const badgeTheme = badgeThemeForRole(active?.role)
  // Greeting prefers the logged-in user's first name (per design). Falls through:
  // firstName -> name (first token) -> username -> email prefix -> mock.
  // The BE currently returns firstName empty on some accounts even though the DB has it;
  // the fallback chain keeps the greeting personal until that's resolved.
  const u = authStore.userDetail
  const greetingName =
    u?.firstName?.trim() ||
    u?.name?.trim()?.split(/\s+/)[0] ||
    u?.username?.trim() ||
    u?.email?.split('@')[0]?.trim() ||
    data.greetingName
  // The active athlete profile (null for coach / none). Drives both the
  // completeness nudge and the dashboard profile card.
  const activeAthlete =
    profileStore.activeProfile?.type === ActiveProfileType.ATHLETE
      ? (raw.athleteProfiles.find(p => p.id === profileStore.activeProfile?.id) ?? null)
      : null

  // BA rule: the "Complete your profile" nudge hides IF AND ONLY IF every
  // edit-profile field is filled; if even one is missing it stays visible.
  // Secondary sports/positions are explicitly optional and `publish` is a
  // separate visibility toggle (not a field), so none of them gate completeness.
  // graduationYear is also optional — a youth athlete may not have graduated yet,
  // so it must NOT gate completeness (otherwise their card never shows).
  const isProfileIncomplete: boolean = (() => {
    if (!activeAthlete) return false
    const ap = activeAthlete
    const allFieldsFilled =
      !!ap.avatarUrl?.trim() &&
      !!ap.firstName?.trim() &&
      !!ap.lastName?.trim() &&
      !!ap.dob &&
      !!ap.gender &&
      !!ap.level?.trim() &&
      typeof ap.height === 'number' &&
      ap.height > 0 &&
      typeof ap.weight === 'number' &&
      ap.weight > 0 &&
      !!ap.primarySport &&
      !!ap.position &&
      !!ap.throws &&
      !!ap.bats
    return !allFieldsFilled
  })()

  // Once complete, the dashboard shows the player's identity banner in place of
  // the nudge — the same PlayerBanner the desktop header carries.
  const profilePreview: ProfilePreview | null = activeAthlete ? athleteProfileToPreview(activeAthlete) : null

  // Banner content. The athlete banner is the player's identity and never carries
  // the organization's colour; the coach's banner IS the organization, so it takes
  // the header slot outright.
  // The dashboard's selection is page-local, but the team detail request is scoped
  // by the app-wide organization. Opening a Team of another organization without
  // switching first is refused as "Team not found in this organization", and the
  // detail page then falls back to the Teams list.
  const openTeam = (orgId: number, teamId: number) => {
    authStore.setActiveOrg(orgId)
    authStore.setActiveTeam(teamId)
    navigate(routes.teams.detail.value(teamId))
  }

  const orgBannerNode = selectedOrg ? (
    <OrgTeamBanner
      orgName={selectedOrg.name}
      sports={selectedTeam ? selectedTeam.sports : selectedOrg.sports}
      title={selectedTeam ? selectedTeam.name : selectedOrg.name}
      logoUrl={selectedOrg.logoUrl}
      primaryColor={selectedOrg.primaryColor}
      // Manage is a coach action, and only once a specific team is in view.
      // No permission check here — /teams/:teamId opens read-only and gates its
      // own edit toggle.
      onManageTeam={isCoachView && selectedTeam ? () => openTeam(selectedOrg.id, selectedTeam.id) : undefined}
    />
  ) : null

  const headerBanner = isCoachView ? (
    orgBannerNode
  ) : (
    <PlayerBanner
      playerName={profilePreview?.fullName ?? greetingName}
      positionLine={profilePreview?.positionLine}
      avatarUrl={profilePreview?.avatarUrl}
      playingFor={organizations.map(o => o.name)}
    />
  )

  const shellProps: IShellProps = {
    data,
    greetingName,
    roleLabel,
    badgeTheme,
    profiles,
    active,
    setActive,
    activeOrgId,
    isProfileIncomplete,
    profilePreview,
    playingFor: organizations.map(o => o.name),
    scopeError,
    scopeSwitcher: (
      <OrgTeamSwitcher
        organizations={organizations}
        selectedOrgId={selectedOrgId}
        onOrgChange={handleOrgChange}
        teams={orgTeams}
        selectedTeamId={selectedTeamId}
        onTeamChange={handleTeamChange}
        isLoading={isTreeLoading}
      />
    ),
    scopeBanner: orgBannerNode,
    isCoachView
  }

  // NOTE: zero-membership empty state intentionally NOT rendered yet — the
  // designer hasn't signed off on the empty-state design. Keep showing the
  // existing mock dashboard UI until the design is finalized. The
  // <EmptyTeamsState> component is kept (./components/empty-teams-state) ready
  // to wire once approved.

  return (
    <>
      {welcomeVariant && (
        <WelcomeJourneyModal isOpen variant={welcomeVariant} orgName={joinedOrgName} onLater={handleCloseWelcome} />
      )}
      {isLoading ? (
        <GlobalSpinner />
      ) : isDesktop ? (
        <DesktopShell
          header={
            <DashboardHeader
              greetingName={greetingName}
              roleLabel={roleLabel}
              badgeTheme={badgeTheme}
              banner={headerBanner}
              streak={<StreakBanner plan={data.rallyDay} variant="compact" />}
              organizations={organizations}
              selectedOrgId={selectedOrgId}
              onOrgChange={handleOrgChange}
              teams={orgTeams}
              selectedTeamId={selectedTeamId}
              onTeamChange={handleTeamChange}
              isSwitcherLoading={isTreeLoading}
            />
          }
          // A coach already sees the organization banner in the header — only the
          // athlete gets the second, full-width one below.
          orgBanner={!isCoachView ? orgBannerNode : null}
          quickStats={
            <Box>
              {scopeError && (
                <Box mb="10px">
                  <ScopeErrorNotice message={scopeError} />
                </Box>
              )}
              <Text
                color="#ADB2BF"
                fontSize="12px"
                fontWeight={600}
                lineHeight="18px"
                letterSpacing="0.02em"
                textTransform="uppercase"
                m={0}
                mb="12px"
              >
                Quick Stats
              </Text>
              <QuickStatsGrid stats={data.quickStats} />
            </Box>
          }
          // OrgTeamSwitcher in the header is the scope control here; MobileShell
          // renders the same switcher against the same selection state.
          nextUp={
            data.nextUpCards.length > 0 ? (
              <NextUpScrollRow cards={data.nextUpCards} rightMeta={data.rallyDay.nextUpTime} />
            ) : null
          }
          calendar={
            // A page-turn re-reads the schedule without the page spinner (that
            // waits on the selection only); the panel keeps the window it has
            // until the next one arrives.
            <CalendarPanel
              events={data.calendarEvents}
              mode={calendarMode}
              selectedDate={calendarDate}
              onModeChange={setCalendarMode}
              onSelectedDateChange={setCalendarDate}
            />
          }
          todo={<TodoSidebar todos={data.todos} />}
        />
      ) : (
        <MobileShell {...shellProps} />
      )}
    </>
  )
}

export default observer(PlayDashboardPage)
